import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from 'react';
import { usePlayerContext } from '../Context';
import { StateChangedEvents, CurrentTimeEvents } from '../../sdk/PlayerEvents';
import { Control, Events, State } from '../../types/player';
import Utils from '../../utils/Utils';

const ProgessBar = () => {
  const { sdk } = usePlayerContext();
  const [dragging, setDragging] = useState(false);
  const [pauseOnDragging, setPauseOnDragging] = useState(false);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const [currentTimeInPercentage, setCurrentTimeInPercentage] = useState(
    sdk.getCurrentTime() as number,
  );
  const [currentBufferLoadedInPercentage, setCurrentBufferLoadedInPercentage] =
    useState(0 as number);
  const [duration, setCurrentDuration] = useState(sdk.getDuration() as number);
  const [hoverTime, setHoverTime] = useState<number | null>(null);
  const [isHovering, setIsHovering] = useState(false);

  const progressBar = sdk.options().getControls().getProgressBar();

  let currentDuration = duration;

  let listeners: (() => void)[] = [];
  useEffect(() => {
    listeners.push(
      sdk.on(Events.CURRENT_TIME, (data: CurrentTimeEvents) => {
        if (currentDuration != data.duration && !Number.isNaN(data.duration)) {
          currentDuration = data.duration;
          setCurrentDuration(data.duration);
        }
        setCurrentTimeInPercentage(data.currentTimeInPercentage);
      }),
    );

    listeners.push(
      sdk.on(Events.PROGRESS, (currentBufferLoadedInPercentage: number) => {
        if (currentBufferLoadedInPercentage > 100)
          currentBufferLoadedInPercentage = 100;
        setCurrentBufferLoadedInPercentage(currentBufferLoadedInPercentage);
      }),
    );

    return () => {
      listeners.forEach((listener) => listener());
    };
  }, []);

  useEffect(() => {
    if (dragging || isHovering) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleTouchEnd);
    } else {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [dragging, isHovering]);

  const handleMouseMove = (event: MouseEvent) => {
    if (dragging || isHovering) {
      const { percentage, newTime } = handleSliderMove(event.clientX);
      if (
        newTime &&
        progressBar &&
        progressBar.hoverShow &&
        progressBar.hover == 'time' &&
        isHovering
      )
        setHoverTime(newTime);
      if (dragging) {
        if (percentage) setCurrentTimeInPercentage(percentage);
        if (newTime) sdk.seek(newTime);
      }
    }
  };
  const handleMouseDown = () => {
    setDragging(true);
    const state = sdk.getCurrentState();
    if (state === State.PLAYING) {
      setPauseOnDragging(true);
      sdk.pause();
    }
  };

  const handleMouseUp = () => {
    setDragging(false);
    if (pauseOnDragging) {
      setPauseOnDragging(false);
      sdk.play();
    }
  };

  const handleTouchStart = (event: React.TouchEvent) => {
    setDragging(true);
    const touch = event.touches[0];
    handleSliderMove(touch.clientX);
  };

  const handleTouchMove = (event: TouchEvent) => {
    console.log('ON MOVE', dragging);
    if (dragging) {
      const touch = event.touches[0];
      const { newTime } = handleSliderMove(touch.clientX);
      if (newTime) sdk.seek(newTime);
      event.preventDefault();
    }
  };

  const handleTouchEnd = () => {
    setDragging(false);
  };

  const handleSliderMove = (clientX: number) => {
    let percentage: number | undefined = undefined;
    let newTime: number | undefined = undefined;

    const progressBar = progressBarRef.current;
    if (progressBar) {
      const rect = progressBar.getBoundingClientRect();
      percentage = Math.min(
        Math.max(((clientX - rect.left) / rect.width) * 100, 0),
        100,
      );
      newTime = (percentage / 100) * duration;
    }
    return { percentage, newTime };
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const { newTime } = handleSliderMove(event.clientX);
    if (newTime) sdk.seek(newTime);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
    if (progressBar?.hover && progressBar.hover == 'time') setHoverTime(null);
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    /*if (progressBarRef.current) {
      const rect = progressBarRef.current.getBoundingClientRect();
      let newTime = sdk.getCurrentTime();
      if (event.key === 'ArrowRight') {
        newTime = Math.min(newTime + 10, duration); // seek forward by 5 seconds
      } else if (event.key === 'ArrowLeft') {
        newTime = Math.max(newTime - 10, 0); // seek backward by 5 seconds
      }
      sdk.seek(newTime);
    }*/
  };
  if (
    sdk.isLive() &&
    sdk
      .options()
      .getControls()
      .getLiveAutoHide()
      ?.includes(Control.PROGRESS_BAR)
  )
    return null;

  return (
    <div
      className={`${sdk.getClassNamePrefix()}-player-progressbar ${sdk.getClassNamePrefix()}-player-progressbar-${Utils.optionsToClassName(progressBar?.position as string)}`}
      ref={progressBarRef}
      role="slider"
      aria-valuenow={currentTimeInPercentage}
      aria-valuemin={0}
      aria-valuemax={100}
      //tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseDown={handleMouseDown}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
    >
      {hoverTime !== null && isHovering && (
        <div
          className={`${sdk.getClassNamePrefix()}-player-progressbar-hover-time`}
          style={{ left: `${(hoverTime / duration) * 100}%` }}
        >
          {/* //TODO: add a hook here to be able to bypass this formatting */}

          {sdk.isDvr()
            ? `- ${Utils.convertTimeInSecondsToTimeLabel(duration - hoverTime, duration >= 3600)}`
            : Utils.convertTimeInSecondsToTimeLabel(
                hoverTime,
                duration >= 3600,
              )}
        </div>
      )}
      <div
        className={`${sdk.getClassNamePrefix()}-player-progressbar-container`}
      >
        <div
          className={`${sdk.getClassNamePrefix()}-player-progressbar-progress`}
          style={{ width: `${currentTimeInPercentage}%` }}
        >
          <div
            className={`${sdk.getClassNamePrefix()}-player-progressbar-thumb`}
          ></div>
        </div>
        {isHovering && hoverTime != null && (
          <div
            className={`${sdk.getClassNamePrefix()}-player-progressbar-hover-bar`}
            style={{ width: `${(hoverTime / duration) * 100}%` }}
          ></div>
        )}
        <div
          className={`${sdk.getClassNamePrefix()}-player-progressbar-loaded`}
          style={{ width: `${currentBufferLoadedInPercentage}%` }}
        ></div>
      </div>
    </div>
  );
};

export default ProgessBar;
